import { ArticleTeaser } from "@/components/ArticleTeaser";
import { CoverImage } from "@/components/CoverImage";
import { Footer } from "@/components/Footer";
import { Header } from "@/components/Header";
import { PageHeader } from "@/components/PageHeader";
import { PageInfo } from "@/components/PageInfo";
import { Deployment, deploymentFromContext } from "@/domain/deployment";
import { DEFAULT_LANGUAGE, useI18n } from "@/domain/i18n";
import { MenuItem } from "@/domain/menu";
import { fetchMenuItems } from "@/domain/menu.ssr";
import { AboutPage, ContactPage, DefaultPage, foldPage_, NewsPage, Page } from "@/domain/page";
import { fetchPage } from "@/domain/page.ssr";
import { Person, Sponsor } from "@/domain/people";
import { useAccordion } from "@/hooks/useAccordion";
import { useVideo } from "@/hooks/useVideo";
import { chunks } from "@/lib/array.extended";
import { GetServerSideProps, GetServerSidePropsContext, InferGetServerSidePropsType } from "next";
import dynamic from "next/dynamic";
import Head from "next/head";
import React from "react";

const OrganizationsTemplate = dynamic(() => import("../../templates/OrganizationsTemplate"), {
  ssr: false,
});

export const getServerSideProps: GetServerSideProps<{
  page: Page;
  menuItems: Array<MenuItem>;
  deployment: Deployment;
}> = async (ctx: GetServerSidePropsContext) => {
  const deployment = deploymentFromContext(ctx);
  const locale = ctx.locale ?? DEFAULT_LANGUAGE;
  const page_id = ctx.params && typeof ctx.params.page_id === "string" ? ctx.params.page_id : null;
  if (!page_id) {
    return {
      notFound: true,
    };
  }

  const [page, menuItems] = await Promise.all([
    fetchPage(deployment, locale, page_id),
    fetchMenuItems(deployment, locale),
  ]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: { page, menuItems, deployment },
  };
};

export default function ContentPage({
  page,
  menuItems,
  deployment,
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
  useVideo();
  const footerRef = React.useRef<HTMLDivElement>(null);

  return (
    <>
      <PageInfo {...page.meta} deployment={deployment} />
      <div className="layout-sticky__page">
        <header className="site-header site-header--opaque">
          <div className="site-header__content">
            <Header menuItems={menuItems} deployment={deployment} />
          </div>
        </header>
        <section>
          {foldPage_(page, {
            default: (p) => <DefaultTemplate {...p} />,
            about: (p) => <AboutTemplate {...p} />,
            contact: (p) => <ContactTemplate page={p} footerRef={footerRef} />,
            news: (p) => <NewsTemplate {...p} />,
            organizations: (p) => <OrganizationsTemplate {...p} deployment={deployment} />,
          })}
        </section>
      </div>

      <div
        className={foldPage_(page, {
          default: () => "",
          about: () => "",
          contact: () => "layout-sticky--slim",
          news: () => "",
          organizations: () => "",
        })}
      >
        <div className="layout-sticky__footer" data-spacer-element="footer" ref={footerRef}>
          <Footer
            slim={foldPage_(page, {
              default: () => false,
              about: () => false,
              contact: () => true,
              news: () => false,
              organizations: () => false,
            })}
          />
        </div>
      </div>
    </>
  );
}

// -----------------------------------------------------------------------------
// DefaultTemplate

function DefaultTemplate(page: DefaultPage) {
  const ref = useAccordion<HTMLDivElement>();
  return (
    <>
      <PageHeader title={page.meta.title} intro={page.meta.intro} quote={page.meta.quote} />
      <div className="layout-content group">
        {page.meta.content && (
          <div ref={ref} className="context-text span-3of5" dangerouslySetInnerHTML={{ __html: page.meta.content }} />
        )}
        {page.meta.aside && (
          <div className="context-aside span-2of5 plxxl" dangerouslySetInnerHTML={{ __html: page.meta.aside }} />
        )}
      </div>
    </>
  );
}

// -----------------------------------------------------------------------------
// AboutTemplate

function AboutTemplate(page: AboutPage) {
  const I18n = useI18n();
  return (
    <>
      <PageHeader title={page.meta.title} intro={page.meta.intro} quote={page.meta.quote} />
      <div className="layout-content group">
        {page.teamMembers.length > 0 && (
          <>
            <h2 className="heading-section mbm">{I18n.t("people.team.title")}</h2>
            <div className="portrait-group">
              <PeopleGrid people={page.teamMembers} />
            </div>
          </>
        )}
        {page.boardMembers.length > 0 && (
          <>
            <h2 className="heading-section mbm">{I18n.t("people.board.title")}</h2>
            <div className="portrait-group">
              <PeopleGrid people={page.boardMembers} />
            </div>
          </>
        )}
        {page.sponsors.length > 0 && (
          <>
            <h2 className="heading-section mbm">{I18n.t("people.sponsors.title")}</h2>
            <div className="sponsor-group">
              <SponsorGrid sponsors={page.sponsors} />
            </div>
          </>
        )}
      </div>
    </>
  );
}

function PeopleGrid({ people }: { people: Array<Person> }) {
  const I18n = useI18n();
  return (
    <>
      {chunks(4, people).map((row, i) => (
        <div key={`row-${i}`} className="portrait-row">
          {row.map((person) => (
            <article key={person.name} className="portrait">
              <div className="portrait__photo">
                <CoverImage url={person.photo_path} />
              </div>

              <h2 className="portrait__name">{person.name}</h2>

              <div className="portrait__description">
                <p>{person.description}</p>
              </div>

              <dl className="portrait__list">
                {person.profession && (
                  <>
                    <dt className="portrait__list__key">{I18n.t("people.portrait.profession")}</dt>
                    <dd className="portrait__list__value">{person.profession}</dd>
                  </>
                )}

                {person.email && (
                  <>
                    <dt className="portrait__list__key"></dt>
                    <dd className="portrait__list__value">
                      <a href={`mailto:${person.email}`} className="link link--light">
                        {person.email}
                      </a>
                    </dd>
                  </>
                )}
              </dl>
            </article>
          ))}
        </div>
      ))}
    </>
  );
}

function SponsorGrid({ sponsors }: { sponsors: Array<Sponsor> }) {
  return (
    <>
      {chunks(4, sponsors).map((row, i) => (
        <div key={`row-${i}`} className="sponsor-row">
          {row.map((sponsor) => (
            <article key={sponsor.name} className="sponsor">
              <a className="sponsor__link" href={sponsor.url} target="_blank" rel="noreferrer">
                <h3 className="sponsor__name">{sponsor.name}</h3>
                <div className="sponsor__photo">
                  <CoverImage url={sponsor.photo_path} />
                </div>
              </a>
            </article>
          ))}
        </div>
      ))}
    </>
  );
}

// -----------------------------------------------------------------------------
// ContactTemplate

function ContactTemplate({
  page,
  footerRef,
}: {
  page: ContactPage;
  footerRef: React.RefObject<null | HTMLDivElement>;
}) {
  const I18n = useI18n();
  const mapRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    let map: any;

    async function render() {
      /*
       * Can't import leaflet statically because it's not compatible with SSR.
       */
      const L = await import("leaflet");

      if (mapRef.current != null && footerRef.current != null) {
        const LAT_LNG = I18n.t("contact.marker.coordinates");
        const mapEl = mapRef.current;
        const footer = footerRef.current;

        // Fit map container to window
        const fitMapInWindow = function () {
          const spaceTop = mapEl.getBoundingClientRect().top;
          const spaceBottom = footer.getBoundingClientRect().height;
          const mapHeight = Math.max(200, window.innerHeight - (spaceTop + spaceBottom));
          mapEl.style.height = mapHeight + "px";
        };
        window.addEventListener("resize", fitMapInWindow);
        fitMapInWindow();

        // Render map
        map = L.map(mapEl, {
          minZoom: 5,
          maxZoom: 17,
          zoomControl: false,
        });
        map.setView(LAT_LNG, 15);
        L.tileLayer(
          `https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token=${process.env.MAPBOX_ACCESS_TOKEN}`,
          {
            id: "mapbox/streets-v11",
          },
        ).addTo(map);

        map.dragging.disable();
        map.scrollWheelZoom.disable();
        map.touchZoom.disable();
        if (map.tap) map.tap.disable();

        new L.Control.Zoom({ position: "topright" }).addTo(map);

        L.popup({
          closeButton: false,
          closeOnClick: false,
          keepInView: true,
          minWidth: 380,
          maxWidth: 600,
        })
          .setLatLng(LAT_LNG as any as [number, number])
          .setContent(
            `<div class="context-mapbox"><h2 class="title">${I18n.t("contact.marker.title")}</h2><p>${simpleFormat(
              I18n.t("contact.marker.description"),
            )}</p></div>`,
          )
          .openOn(map);
      }
    }

    render();

    return () => {
      map && map.remove();
    };
  }, [I18n, footerRef]);

  return (
    <>
      <Head>
        <style
          type="text/css"
          dangerouslySetInnerHTML={{
            __html: `
              #map { width: 100%; min-height: 200px; position:relative; }
            `,
          }}
        ></style>
      </Head>

      <header className="page-header">
        <div className="page-header__primary">
          <div className="page-header__content">
            <h1 className="page-header__title">{page.meta.title}</h1>
          </div>
        </div>
        <div className="page-header__secondary">
          <div className="page-header__content">
            <div className="clearfix">
              <ul className="page-header__list kv-list kv-list--inverted float-left">
                <li className="kv-list__item">
                  <span className="kv-list__value">
                    <a href={`mailto:${I18n.t("footer.email")}`} className="button button--inline button--secondary">
                      <i className="icon-mail"></i> {I18n.t("footer.email")}
                    </a>
                  </span>
                </li>
              </ul>
              <ul className="page-header__list kv-list kv-list--inverted float-right">
                <li className="kv-list__item">
                  <span className="kv-list__key">{I18n.t("frontend.social-media.follow.label")}</span>
                  <span className="kv-list__value">
                    <a
                      href="https://www.facebook.com/publicvalueresearch"
                      className="button button--inline button--secondary mls"
                      target="_blank"
                      rel="noreferrer"
                    >
                      <i className="icon-facebook"></i> {I18n.t("frontend.social-media.follow.facebook")}
                    </a>
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </header>

      <div id="map" ref={mapRef}></div>
    </>
  );
}

function simpleFormat(str: string) {
  return str.split("\n").join("<br />");
}

// -----------------------------------------------------------------------------
// NewsTemplate

function NewsTemplate(page: NewsPage) {
  const I18n = useI18n();
  return (
    <>
      <PageHeader title={page.meta.title} intro={page.meta.intro} quote={page.meta.quote} />
      <div className="layout-content group">
        <div className="teaser-group" data-teaser-group>
          {page.newsItems.length > 0 ? (
            <>
              {chunks(3, page.newsItems).map((row, i) => (
                <div key={i} className="teaser-row">
                  {row.map((item, j) => (
                    <ArticleTeaser key={j} item={item} />
                  ))}
                </div>
              ))}
            </>
          ) : (
            <p>{I18n.t("articles.no-news")}</p>
          )}
        </div>
      </div>
    </>
  );
}
